import type { ActionRequest, Decision, ReviewConfig } from 'langchain';
import type { BaseDataMessage } from './base-message.types';

export type HITLActionRequest = ActionRequest & {
  id?: string;
  toolCallId?: string;
  elevated?: boolean;
  allowRulePattern?: string;
  allowToolRule?: string;
  reviewReason?: string;
};

export type HITLDecision = Decision & {
  id?: string;
  toolCallId?: string;
};

export enum ConfirmationMessageTypeEnum {
  NodeConfirmation = 'data-nodeConfirmation',
  StopConfirmation = 'data-stopConfirmation',
  GuardrailConfirmation = 'data-guardrailConfirmation',
  UserConfirmation = 'data-confirmation',
  HITLRequestConfirmation = 'data-hitlRequestConfirmation',
  AskUser = 'data-askUser',
  TerminalOutput = 'terminal-output',
  ExternalToolResults = 'external-tool-results',
}

export const StreamConfirmationPartTypeName = 'data-confirmationPrompt';

export type ConfirmationMessageType =
  | StopConfirmationMessage
  | GuardrailConfirmationMessage
  | NodeConfirmationMessage
  | UserConfirmationMessage
  | HITLRequestConfirmationMessage
  | AskUserMessage;

export type PromptOption = {
  label: string;
  value: string;
};

export interface UserConfirmationMessage extends BaseDataMessage {
  type: ConfirmationMessageTypeEnum.UserConfirmation;
  subs: string[];
  response: string;
  message?: string;
  toolName?: string;
  decision?: HITLDecision;
  decisions?: HITLDecision[];
  actionRequest?: HITLActionRequest;
}

export interface NodeConfirmationMessage extends BaseDataMessage {
  type: ConfirmationMessageTypeEnum.NodeConfirmation;
  nodeConfirmationPrompt?: {
    nodeName: string;
    description: string;
    options: PromptOption[];
    cycle?: number;
    args?: Record<string, unknown>;
  };
}

export interface StopConfirmationMessage extends BaseDataMessage {
  type: ConfirmationMessageTypeEnum.StopConfirmation;
  stopConfirmationPrompt?: {
    title: string;
    description: string;
    stopReason?: string;
    evidence?: string;
    options: PromptOption[];
  };
  selectedOption?: string;
}

export interface GuardrailConfirmationMessage extends BaseDataMessage {
  type: ConfirmationMessageTypeEnum.GuardrailConfirmation;
  guardrailConfirmationPrompt?: {
    title: string;
    description: string;
    toolName: string;
    violatedGuardrails: string[];
    reason: string;
    options: PromptOption[];
    args?: Record<string, unknown>;
  };
  selectedOption?: string;
  message?: string;
}

export interface HITLRequestConfirmationMessage extends BaseDataMessage {
  type: ConfirmationMessageTypeEnum.HITLRequestConfirmation;
  hitlRequestConfirmationPrompt?: {
    title: string;
    description: string;
    options: PromptOption[];
    actionRequests: HITLActionRequest[];
    reviewConfigs: ReviewConfig[];
  };
  decision?: HITLDecision;
  backgroundSubagent?: {
    taskId: string;
    toolCallId: string;
    copilotThreadId?: string;
  };
}

export type TerminalFailureKind =
  | 'command'
  | 'timeout'
  | 'abort'
  | 'tracker'
  | 'transport';

export type TerminalOutputItem = {
  batchId?: string;
  interruptId?: string;
  toolCallId: string;
  command: string;
  exitCode: number;
  output: string;
  outputLength: number;
  isTimeout?: boolean;
  failureKind?: TerminalFailureKind;
  timedOut?: boolean;
  idleTimedOut?: boolean;
  backgrounded?: boolean;
  jobId?: string;
  jobStatus?: 'running' | 'exited' | 'killed';
  logPath?: string;
  cwdAfter?: string;
  durationMs?: number;
  truncated?: boolean;
  outputBytes?: number;
  shell?: string;
  platform?: string;
  ptyId?: string;
  ptyAlive?: boolean;
  screen?: string;
};

export interface TerminalOutputMessage
  extends BaseDataMessage,
    TerminalOutputItem {
  type: ConfirmationMessageTypeEnum.TerminalOutput;
}

export type ExternalToolResultItem = {
  batchId?: string;
  interruptId?: string;
  toolCallId: string;
  toolName: string;
  status: 'success' | 'error';
  result: unknown;
};

export interface ExternalToolResultsMessage extends BaseDataMessage {
  type: ConfirmationMessageTypeEnum.ExternalToolResults;
  batchId: string;
  results: ExternalToolResultItem[];
}

export type HITLRequestConfirmationDecision =
  HITLRequestConfirmationMessage['decision'];

export interface AskUserMessage extends BaseDataMessage {
  type: ConfirmationMessageTypeEnum.AskUser;
  askUserPrompt: {
    prompt: string;
    options: PromptOption[];
  };
}

export type { ActionRequest, Decision, ReviewConfig } from 'langchain';
